import React from 'react';
import styled from 'astroturf/react';
import { SuggestionMetadata } from 'js/social/shared/types';
import { GraduationCapIcon, TagIcon } from 'js/social/shared/Icons';
import { OverlappingAvatars } from 'js/social/shared/OverlappingAvatars';
import PaperContainer from 'js/social/PaperContainer';

const SMALL_PLACEHOLDER_PHOTO = '//a.academia-assets.com/images/s24_no_pic.png';

const Container = styled.div`
  @import 'stylesheets/design_system/font';
  @include sans-serif;
  font-size: 12px;
  line-height: 18px;
  display: flex;
  gap: 8px;
  color: $ds-black;

  :first-child {
    flex-shrink: 0;
  }
`;

const TextEllipsis = styled.span`
  @import 'stylesheets/design_system/_util.scss';
  @include text-ellipsis(2, 18px);
  width: 100%;
`;

const Dot = styled.span`
  display: none;
  width: 12px;
  text-align: center;

  @include for-tablet-up {
    display: inline-block;
  }
`;

const TextSeparator = styled.div`
  display: block;

  @include for-tablet-up {
    display: inline-block;
  }
`;

type Props = {
  source: string;
  metadata: SuggestionMetadata;
  style?: React.CSSProperties;
  className?: string;
};

export const SuggestionReason: React.FC<Props> = ({
  source,
  metadata,
  style,
  className,
}) => {
  if (!(source in metadata)) {
    return null;
  }

  if (source === 'UniversityColleagues') {
    return (
      <Container style={style} className={className}>
        <GraduationCapIcon style={{ marginTop: 3 }} />
        <div>
          <TextEllipsis>
            {metadata.UniversityColleagues.department}
          </TextEllipsis>
        </div>
      </Container>
    );
  } else if (source === 'FollowedByFollowees') {
    const { followees, count } = metadata.FollowedByFollowees;
    const names = followees.map((user) => user.display_name);
    const text =
      count <= 2
        ? names.join(' and ')
        : names.join(', ') + `, +${count - 2} more`;

    return (
      <Container style={style} className={className}>
        <OverlappingAvatars
          users={followees.map((user, index) => ({
            id: index,
            displayName: user.display_name,
            photo: user.photo || SMALL_PLACEHOLDER_PHOTO,
          }))}
        />
        <div>
          <TextEllipsis>Followed by {text}</TextEllipsis>
        </div>
      </Container>
    );
  } else if (source === 'AuthorsBookmarked') {
    const work = metadata.AuthorsBookmarked.works[0];

    return (
      <PaperContainer
        size={'small'}
        work={{
          title: work.title,
          authors: [],
          publication: work.publication ?? undefined,
          publicationYear: work.publication_year ?? undefined,
          attachment: {
            id: null,
            readable: null,
            thumbnailUrl: work.thumbnail,
            pdfUrl: null,
            pageCount: null,
          },
        }}
        style={style}
        className={className}
      />
    );
  } else if (source === 'TopResearchInterestUsers') {
    const ris = metadata.TopResearchInterestUsers.mutual_ris;
    const text =
      ris.length <= 2
        ? ris.slice(0, 2).join(' and ')
        : ris.slice(0, 2).join(', ') + `, +${ris.length - 2} more`;

    return (
      <Container style={style} className={className}>
        <TagIcon style={{ marginTop: 3 }} />
        <div>
          <TextEllipsis>Follows {text}</TextEllipsis>
        </div>
      </Container>
    );
  } else if (source === 'FeaturedResearchInterestUser') {
    return (
      <Container style={style} className={className}>
        <TextEllipsis>
          <TextSeparator>
            {metadata.FeaturedResearchInterestUser.followings} followers
          </TextSeparator>
          <Dot>•</Dot>
          <TextSeparator>
            {metadata.FeaturedResearchInterestUser.papers} papers
          </TextSeparator>
        </TextEllipsis>
      </Container>
    );
  }

  return null;
};
